import { Box, Divider, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { RHFSwitch } from 'src/components/minimals/form';
import { useAuthentication } from 'src/features/authentication/context';
import { CustomerService } from 'src/services/customers/customer.service';
import type { TemplateImageKind } from 'src/services/reports/types';

import { useKindObjectUrls } from '../../hooks/useKindObjectUrls';
import {
  getUploadErrorMessage,
  useUploadTemplateImageMutation,
} from '../../hooks/useUploadTemplateImageMutation';
import { getDesignImageUrl, removeDesignImage, setDesignImage } from '../../utils/templateImages';
import { ImageUploadField } from '../image-upload-field';

// The report centre-crops the cover to this aspect; the site image is never cropped.
const COVER_ASPECT_RATIO = 1.22;

const IMAGE_FIELDS: {
  kind: TemplateImageKind;
  label: string;
  testId: string;
  previewAspectRatio?: number;
}[] = [
  {
    kind: 'cover',
    label: 'Cover Image',
    testId: 'cover-image',
    previewAspectRatio: COVER_ASPECT_RATIO,
  },
  { kind: 'site', label: 'Site Image', testId: 'site-image' },
];

type ByKind<T> = Partial<Record<TemplateImageKind, T>>;

type Props = {
  disabled?: boolean;
  onUploadingChange?: (uploading: boolean) => void;
};

export const ReportDesignSection = ({ disabled, onUploadingChange }: Props) => {
  const { setValue, getValues, watch } = useFormContext();
  const { mutateAsync: uploadTemplateImage } = useUploadTemplateImageMutation();
  const previewUrls = useKindObjectUrls<TemplateImageKind>();
  const [busyKinds, setBusyKinds] = useState<ByKind<boolean>>({});
  const [uploadErrors, setUploadErrors] = useState<ByKind<string>>({});
  const design = watch('data.design');
  const {
    customerId: { value: customerId },
  } = useAuthentication();
  const { data: customer } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => CustomerService.getById(customerId!),
    enabled: !!customerId,
  });
  // Without a report logo the render always shows the STREAMETRIC logo, so the switch shows on
  // without touching the stored value. Loading or a failed read (`{}`) leaves it enabled.
  const isLogoForced = customer?.hasReportLogo === false;

  const isUploading = Object.values(busyKinds).some(Boolean);
  useEffect(() => {
    onUploadingChange?.(isUploading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploading]);

  const setUploadError = (kind: TemplateImageKind, error: string) =>
    setUploadErrors((prev) => ({ ...prev, [kind]: error }));

  // Reads design when the upload settles, so a toggle flipped mid-upload is kept.
  const uploadImage = (kind: TemplateImageKind) => async (file: File) => {
    setUploadError(kind, '');
    try {
      const key = await uploadTemplateImage({ kind, file });
      const image = { key, url: previewUrls.create(kind, file) };
      setValue('data.design', setDesignImage(getValues('data.design'), kind, image));
    } catch (error) {
      setUploadError(kind, getUploadErrorMessage(error));
    }
  };

  const removeImage = (kind: TemplateImageKind) => () => {
    setUploadError(kind, '');
    previewUrls.release(kind);
    setValue('data.design', removeDesignImage(getValues('data.design'), kind));
  };

  return (
    <>
      <Divider sx={{ gridColumn: '1/-1', my: 2 }} />
      <Box sx={{ gridColumn: '1/-1', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle1">Customize Design</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', columnGap: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {IMAGE_FIELDS.map(({ kind, label, testId, previewAspectRatio }) => (
              <ImageUploadField
                key={kind}
                label={label}
                testId={testId}
                previewUrl={getDesignImageUrl(design, kind)}
                previewAspectRatio={previewAspectRatio}
                uploadError={uploadErrors[kind]}
                disabled={disabled}
                onUpload={uploadImage(kind)}
                onRemove={removeImage(kind)}
                onBusyChange={(busy) => setBusyKinds((prev) => ({ ...prev, [kind]: busy }))}
              />
            ))}
          </Box>
          <RHFSwitch
            name="data.design.showStreametricLogo"
            testId="show-streametric-logo-toggle"
            label="Show STREAMETRIC Logo"
            disabled={disabled || isLogoForced}
            checked={isLogoForced || undefined}
            handleChange={(_, checked) => setValue('data.design.showStreametricLogo', checked)}
          />
        </Box>
      </Box>
    </>
  );
};
